/* eslint-disable no-param-reassign */
import dateFormat from 'dateformat';
import { formatDate } from '../../../utils/dateTimeFns';
import { deviceType, timestampExpired } from '../../../config';
import { filterPatientDevices } from '../../../utils/patientRecordHelper';
import { getTranslatedProductType } from '../../../utils/labelHelper';
import { capitalizeFirstLetter } from '../../../utils/stringFns';

function isErrorField(fieldName, errors) {
  // Check if the field is included in the error array
  return errors?.some((x) => x.source?.pointer === fieldName);
}

function setPhoneNumberField(returnValue, data) {
  returnValue.phoneNumber = {
    value: data.mobile || data.phone || '',
  };
}

function transposeDate(inputDate) {
  return formatDate(inputDate, true);
}

export function processAccountStatus(data) {
  if (data?.accountStatus?.error) {
    return data.accountStatus.message;
  }
  if (data?.accountStatus?.created && data?.email) {
    if (data?.accountStatus?.verified) {
      return 'active';
    }
    return 'invited';
  }
  return 'notSetUp';
}

function setAccountStatusField(returnValue, data, labels, errors) {
  if (!isErrorField('accountStatus', errors)) {
    const status = processAccountStatus(data);
    // Error and Empty states are considered equal for accountStatus field
    if (status) {
      returnValue.accountDetails = {
        value: labels[`labels.patientRecord.${status}`] || status,
        // Store the original status key for logic checks
        rawStatus: status,
      };

      const accountLinkTextLabel = `labels.patientRecord.accountLinkText${capitalizeFirstLetter(
        status,
      )}`;

      returnValue.emailVerified = {
        value: labels[accountLinkTextLabel],
      };

      if (returnValue.emailVerified.value) {
        // Add accountLinkSeparator (-) only if email link is not empty
        returnValue.accountLinkSeparator = {
          value: labels['labels.patientRecord.accountLinkSeparator'],
        };
      }
    } else {
      returnValue.accountDetails = {
        value: labels[`labels.patientRecord.notApplicable`],
      };
    }
  } else {
    // Add "Error loading" message if accountStatus field is in the errors list
    // or show "deceased", if patient/carer is deceased
    returnValue.accountDetails = data.isDeceased
      ? { value: labels[`labels.patientRecord.deceased`] }
      : { value: labels[`labels.patientRecord.error`] };
  }
}

function setAddressField(returnValue, data, labels, errors) {
  if (!isErrorField('address', errors)) {
    if (data.address) {
      // Populate address field only if address object is not NULL or EMPTY
      returnValue.addressId = {
        value: data.address?.id ?? '',
      };
      returnValue.street1 = {
        value:
          data.address?.street?.length > 0
            ? data.address?.street[0]
            : '',
      };
      returnValue.street2 = {
        value:
          data.address?.street?.length > 1
            ? data.address?.street[1]
            : '',
      };
      returnValue.city = {
        value: data.address?.city ?? '',
      };
      returnValue.postcode = {
        value: data.address?.postcode ?? '',
      };
      returnValue.state = {
        value: data.address?.state ?? '',
      };
      returnValue.country = {
        value: data.address?.country ?? '',
      };
      returnValue.countryCode = {
        value: data.address?.countryCode ?? '',
      };
    } else {
      returnValue.street1 = {
        value: labels[`labels.patientRecord.dataEmpty`],
      };
    }
    // Map billingAddress and shippingAddress independently of data.address so
    // that underage patients (whose data.address is withheld) still have their
    // dedicated billing/shipping addresses forwarded to the SR Portal.
    if (data.shippingAddress) {
      returnValue.shippingAddress = {
        value: {
          id: data.shippingAddress.id ?? '',
          street1: data.shippingAddress.street?.[0] ?? '',
          street2: data.shippingAddress.street?.[1] ?? '',
          city: data.shippingAddress.city ?? '',
          state: data.shippingAddress.state ?? '',
          postcode: data.shippingAddress.postcode ?? '',
          country: data.shippingAddress.country ?? '',
          countryCode: data.shippingAddress.countryCode ?? '',
        },
      };
    }
    if (data.billingAddress) {
      returnValue.billingAddress = {
        value: {
          id: data.billingAddress.id ?? '',
          street1: data.billingAddress.street?.[0] ?? '',
          street2: data.billingAddress.street?.[1] ?? '',
          city: data.billingAddress.city ?? '',
          state: data.billingAddress.state ?? '',
          postcode: data.billingAddress.postcode ?? '',
          country: data.billingAddress.country ?? '',
          countryCode: data.billingAddress.countryCode ?? '',
        },
      };
    }
  } else {
    returnValue.street1 = {
      value: labels[`labels.patientRecord.error`],
    };
  }
}

const warrantyData = {
  warrantyOptions: [
    { option: 'actions.grouping.active' },
    { option: 'actions.grouping.expires90' },
    {
      option: 'actions.grouping.expired',
      value:
        'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.expired',
    },
    {
      option: 'actions.grouping.notApplicable',
      value:
        'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.notApplicable',
    },
  ],
  abbrSingleYear:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.abbrSingleYear',
  abbrPluralYear:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.abbrPluralYear',
  singleYear:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.singleYear',
  pluralYear:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.pluralYear',
  abbrSingleMonth:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.abbrSingleMonth',
  abbrPluralMonth:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.abbrPluralMonth',
  singleMonth:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.singleMonth',
  pluralMonth:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.pluralMonth',
  singleDay:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.singleDay',
  pluralDay:
    'labels.patientRecord.tabs.equipments.dataTable.columns.warranty.text.pluralDay',
  timestamp: Date.now() - 10000000000, // latestWarrantyDate
};

export const patientData = (data, errors, labels) => {
  const returnValue = {
    _metadata: {
      readOnlyForm: false,
    },

    firstName: {
      value: data.firstName,
      permission: 'rw',
    },
    lastName: {
      value: data.lastName,
      permission: 'rw',
    },
    language: {
      value: data.preferredLanguage ?? '',
    },
    dateOfBirth: {
      value: !Number.isNaN(Date.parse(data.dateOfBirth))
        ? data.dateOfBirth
        : '',
      permission: 'rw',
    },
    dateOfBirthFormatted: {
      value: !Number.isNaN(Date.parse(data.dateOfBirth))
        ? formatDate(data.dateOfBirth)
        : '',
      permission: 'rw',
    },
    familyConsent: {
      value: null,
      permission: 'r',
    },
    phones: {
      value: [data.phone, data.mobile],
      permission: 'rw',
    },
    relatedCarers: {
      value: '',
      permission: 'not applicable',
    },
    accountId: {
      value: data.id,
    },
    isUnderAge: {
      value: data.isUnderAge,
    },
  };

  if (!data.isUnderAge) {
    // Do not show some fields if patient is underAge : address, email, phone number, account status
    setAddressField(returnValue, data, labels, errors);
    setPhoneNumberField(returnValue, data, labels);
  } else {
    // For underage patients, still map billingAddress and shippingAddress so the
    // SR Portal can pre-populate billing/shipping fields from the patient's address list.
    setAddressField(returnValue, data, labels, errors);
  }

  if (
    (data.isUnderAge && data.accountStatus.created) ||
    !data.isUnderAge
  ) {
    setAccountStatusField(returnValue, data, labels, errors);
    returnValue.email = {
      value: data.email,
      permission: 'rw',
    };
  }

  return returnValue;
};

export const getGenericEmptyListData = (labels, viewId) => {
  return {
    type: 'others',
    values: [
      {
        emptyText: {
          value:
            labels[
              `labels.patientRecord.views.${viewId}.read.emptyText`
            ],
        },
      },
    ],
  };
};

export const getGenericErrorData = (labels, viewId) => {
  return {
    type: 'others',
    values: [
      {
        title: {
          value:
            labels[
              `labels.patientRecord.views.${viewId}.read.errorTitle`
            ],
        },
        line1: {
          value:
            labels[
              `labels.patientRecord.views.${viewId}.read.errorLine1`
            ],
        },
        line2: {
          value:
            labels[
              `labels.patientRecord.views.${viewId}.read.errorLine2`
            ],
        },
      },
    ],
  };
};

export const formatPatientCarersData = (carersList, labels) => {
  const returnValue = {
    type: 'others',
    values: carersList.map(({ data, errors }) => {
      let { firstName, lastName } = data;

      if (firstName === 'UPDATENAME' || lastName === 'UPDATENAME') {
        firstName =
          labels['labels.patientRecord.carerDetails.nameMissing'];
        lastName = '';
      }

      const carerObject = {
        accountId: {
          value: data.id,
        },
        firstName: {
          value: firstName,
        },
        lastName: {
          value: lastName,
        },
        email: {
          value: data.email,
        },
        language: {
          value: data.preferredLanguage ?? '',
        },
        dateOfBirth: {
          value: !Number.isNaN(Date.parse(data.dateOfBirth))
            ? dateFormat(data.dateOfBirth, 'yyyy-mm-dd')
            : '',
        },
      };

      setAddressField(carerObject, data, labels, errors);
      setPhoneNumberField(carerObject, data);
      setAccountStatusField(carerObject, data, labels, errors);

      // Optimistic carer has no real ID yet — hide the invite link
      // since we can't act on it without a known carer ID.
      if (!data.id) {
        delete carerObject.emailVerified;
        delete carerObject.accountLinkSeparator;
      }

      return carerObject;
    }),
  };

  return returnValue;
};

export const formatPatientClinicData = (providers) => {
  return {
    type: 'others',
    values: providers.map(
      ({
        name,
        phone,
        id,
        address: { street, city, state, postcode, country },
      }) => ({
        clinicId: {
          value: id,
        },
        clinicName: {
          value: name,
        },
        phoneNumber: {
          value: phone,
        },
        street1: {
          value: street,
        },
        street2: {
          value: '',
        },
        city: {
          value: city,
        },
        state: {
          value: state,
        },
        postcode: {
          value: postcode,
        },
        country: {
          value: country,
        },
      }),
    ),
  };
};

function setLabel(isLoaner, isCurrent, labels) {
  if (isLoaner === true) {
    return labels[`labels.patientRecord.tabs.equipments.loaner`];
  }
  if (isCurrent === true) {
    return labels[
      `labels.patientRecord.tabs.equipments.currentDevice`
    ];
  }
  return '';
}

function getLoanerContextMenuItem(
  isLoaner,
  serviceRequestId,
  hasOpenSR,
) {
  if (isLoaner !== true || serviceRequestId == null) {
    return null;
  }

  return hasOpenSR
    ? 'viewServiceRequestForRecipientDevice'
    : 'returnLoaner';
}

function normalizeProductType(productType) {
  return typeof productType === 'string'
    ? productType.toLowerCase()
    : '';
}

function getNormalizedPatientDevices(deviceList) {
  if (Array.isArray(deviceList)) {
    return deviceList;
  }

  return filterPatientDevices(deviceList) ?? [];
}

export const formatPatientDeviceData = (deviceList, labels) => {
  const filteredDevices = getNormalizedPatientDevices(deviceList);
  const now = new Date();
  const contextMenuMap = filteredDevices
    .map(
      ({
        deviceId,
        isNewServiceRequestEnabled,
        isViewServiceRequestEnabled,
        isReportAnIssueEnabled,
        productType,
        earSide,
        serialNumber,
        isSerialControlled,
        isLoaner,
        hasOpenSR,
        serviceRequestId,
        endOfServiceDate,
      }) => {
        let returnValue = {};
        const contextMenu = [];
        const isNonSerialControlled =
          typeof isSerialControlled === 'boolean'
            ? !isSerialControlled
            : serialNumber === null;
        const parsedEndOfServiceDate = endOfServiceDate
          ? new Date(endOfServiceDate)
          : null;
        const isEndOfServiceDateValid = Boolean(
          parsedEndOfServiceDate &&
            !Number.isNaN(parsedEndOfServiceDate.getTime()),
        );
        const hasPassedEndOfServiceDate =
          isEndOfServiceDateValid && now > parsedEndOfServiceDate;
        const isSrEosProduct =
          productType === 'Sound Processor' ||
          productType === 'Actuator Unit';

        const shouldEnableCreateSrForEosWarning =
          isSrEosProduct &&
          hasPassedEndOfServiceDate &&
          !isViewServiceRequestEnabled &&
          !hasOpenSR;

        if (
          isNewServiceRequestEnabled ||
          shouldEnableCreateSrForEosWarning
        ) {
          contextMenu.push('newServiceRequest');
        }
        if (isViewServiceRequestEnabled && !isLoaner) {
          contextMenu.push('viewServiceRequest');
        }
        if (isReportAnIssueEnabled) {
          contextMenu.push('reportAnIssue');
        }
        if (
          (productType === 'Sound Processor' ||
            productType === 'Implant') &&
          (earSide === 'Left' || earSide === 'Unknown')
        ) {
          contextMenu.push('assignEarRight');
        }
        if (
          (productType === 'Sound Processor' ||
            productType === 'Implant') &&
          (earSide === 'Right' || earSide === 'Unknown')
        ) {
          contextMenu.push('assignEarLeft');
        }
        if (
          productType === 'Accessory' &&
          !isViewServiceRequestEnabled &&
          isNonSerialControlled
        ) {
          contextMenu.push('replaceAccessory');
        }
        const loanerContextMenuItem = getLoanerContextMenuItem(
          isLoaner,
          serviceRequestId,
          hasOpenSR,
        );
        if (loanerContextMenuItem) {
          contextMenu.push(loanerContextMenuItem);
        }

        if (contextMenu.length > 0) {
          returnValue = {
            id: !deviceId ? '-1' : deviceId,
            items: contextMenu,
          };
        }
        return returnValue;
      },
    )
    .filter((menuItem) => menuItem.id && menuItem.items?.length > 0);

  const getModelInfo = (productType, serialNumber, lotNumber) => {
    const normalizedProductType = normalizeProductType(productType);
    let info = '';
    if (serialNumber) {
      info = `${labels['labels.patientRecord.tabs.equipments.serialNumber']} ${serialNumber}`;
    } else if (lotNumber) {
      info = `${labels['labels.patientRecord.tabs.equipments.lotNumber']} ${lotNumber}`;
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        normalizedProductType === deviceType.Accessory ||
        normalizedProductType === deviceType.RemoteAssistant
      ) {
        info = '';
      } else {
        info =
          labels[
            'labels.patientRecord.tabs.equipments.serialOrLotNumber'
          ];
      }
    }
    return info;
  };

  const data = filteredDevices.map(
    ({
      assetId,
      deviceId,
      // productName,
      serialNumber,
      lotNumber,
      productType,
      productDescription,
      // status,
      earSide,
      startDate,
      // endDate,
      isCurrent,
      warrantyExpiration,
      serviceRequestId,
      serviceRequestNumber,
      isLoaner,
      partNumber,
      productId,
    }) => {
      const normalizedProductType = normalizeProductType(productType);
      let timestamp = null;

      if (warrantyExpiration) {
        if (normalizedProductType !== deviceType.Implant) {
          timestamp = new Date(warrantyExpiration).getTime();
        }
      } else if (
        normalizedProductType === deviceType.SoundProcessor
      ) {
        // if startDate is less than 5 days ago, then set timestamp to null, otherwise, set timestamp to a past date
        const recentDate = new Date();
        recentDate.setDate(recentDate.getDate() - 5); // 5 days ago
        if (new Date(startDate) < recentDate) {
          timestamp = timestampExpired; // date in past to display Expired (Sound Processor with no entitlements)
        }
      }

      return {
        assetId: !assetId ? '-1' : assetId,
        deviceId: !deviceId ? '-1' : deviceId,
        id: !deviceId ? '-1' : deviceId,
        productId: !productId ? '-1' : productId,
        serviceRequestId: !serviceRequestId ? '-1' : serviceRequestId,
        serviceRequestNumber: serviceRequestNumber || '',
        current: isCurrent,
        startDate: startDate || '',
        icon: !(
          normalizedProductType === deviceType.Accessory ||
          normalizedProductType === deviceType.ActuatorUnit ||
          normalizedProductType === deviceType.RemoteAssistant ||
          earSide === 'Unknown'
        ),
        earLeft: earSide === 'Left',
        isUnknownEarSIde: earSide === 'Unknown',
        loaner: isLoaner,
        device: {
          type: !productType ? '' : productType,
          status: setLabel(isLoaner, isCurrent, labels),
          label: getTranslatedProductType(productType || '', labels),
        },
        model: {
          name: productDescription,
          info: getModelInfo(productType, serialNumber, lotNumber),
          partNumber,
        },
        warranty: {
          ...warrantyData,
          timestamp,
        },
      };
    },
  );

  const returnValue = {
    data,
  };
  if (contextMenuMap.length > 0) {
    returnValue.contextMenuMap = contextMenuMap;
  }
  return returnValue;
};

export const formatExpandedData = (device, labels) => {
  let data = {};
  /* eslint-disable prefer-const */
  let regWarranty = device?.device?.entitlements?.find(
    (warranty) =>
      warranty.warrantyName !== 'Extended Warranty' &&
      warranty.warrantyName !== 'Supplier Warranty',
  );
  let extWarranty = device?.device?.entitlements?.find(
    (warranty) => warranty.warrantyName === 'Extended Warranty',
  );
  let supWarranty = device?.device?.entitlements?.find(
    (warranty) => warranty.warrantyName === 'Supplier Warranty',
  );
  let dev = device?.device;
  switch (normalizeProductType(device?.device?.productType)) {
    case deviceType.Implant:
      data = {
        expandedDataL: [
          {
            label:
              labels['labels.patientRecord.tabs.equipments.surgeon'],
            value: dev?.professional?.name,
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.surgeryCenter'
              ],
            value: dev?.organisation?.name,
          },
        ],
        expandedDataR: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.surgeryDate'
              ],
            value: transposeDate(dev?.startDate),
          },
        ],
      };

      break;

    case deviceType.SoundProcessor:
      data = {
        expandedDataL: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.audiologist'
              ],
            value: dev?.professional?.name,
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.fittingClinic'
              ],
            value: dev?.organisation?.name,
          },
        ],
        expandedDataR: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.fittingDate'
              ],
            value: transposeDate(dev?.startDate),
          },
          {
            label:
              labels['labels.patientRecord.tabs.equipments.warranty'],
            value: transposeDate(regWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.extWarranty'
              ],
            value: transposeDate(extWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.supWarranty'
              ],
            value: transposeDate(supWarranty?.warrantyExpiration),
          },
        ],
      };
      break;

    case deviceType.Accessory:
      data = {
        expandedDataL: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.partnumber'
              ],
            value: dev?.partNumber,
          },
        ],
        expandedDataR: [
          {
            label:
              labels['labels.patientRecord.tabs.equipments.warranty'],
            value: transposeDate(regWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.extWarranty'
              ],
            value: transposeDate(extWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.supWarranty'
              ],
            value: transposeDate(supWarranty?.warrantyExpiration),
          },
        ],
      };
      break;

    case deviceType.RemoteAssistant:
      data = {
        expandedDataL: [],
        expandedDataR: [
          {
            label:
              labels['labels.patientRecord.tabs.equipments.warranty'],
            value: transposeDate(regWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.extWarranty'
              ],
            value: transposeDate(extWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.supWarranty'
              ],
            value: transposeDate(supWarranty?.warrantyExpiration),
          },
        ],
      };
      break;

    case deviceType.ActuatorUnit:
      data = {
        expandedDataL: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.audiologist'
              ],
            value: dev?.professional?.name,
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.fittingClinic'
              ],
            value: dev?.organisation?.name,
          },
        ],
        expandedDataR: [
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.fittingDate'
              ],
            value: transposeDate(dev?.startDate),
          },
          {
            label:
              labels['labels.patientRecord.tabs.equipments.warranty'],
            value: transposeDate(regWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.extWarranty'
              ],
            value: transposeDate(extWarranty?.warrantyExpiration),
          },
          {
            label:
              labels[
                'labels.patientRecord.tabs.equipments.supWarranty'
              ],
            value: transposeDate(supWarranty?.warrantyExpiration),
          },
        ],
      };
      break;

    default:
      break;
  }

  if (!data) {
    return [];
  }
  return data;
};

export const getDeviceRegInProgressLabel = (deviceRegInProgress) => {
  if (
    deviceRegInProgress &&
    deviceRegInProgress.toUpperCase() === 'IMPLANT'
  ) {
    return 'labels.patientRecord.views.notification.success.implant';
  }
  if (
    deviceRegInProgress &&
    deviceRegInProgress.toUpperCase() === 'SPEECH_PROCESSOR'
  ) {
    return 'labels.patientRecord.views.notification.success.soundprocessor';
  }
  return 'labels.patientRecord.views.notification.success.accessory';
};
